import { useState, useEffect, useCallback, useRef } from "react";
import { X, Edit2, Search, Mic, MicOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { sessionService } from "../../../application/SessionApplicationService";
import { getSessionTemplate, listSessionTemplates } from "../../../application/sessionTemplates";
import NotionEditor, { NotionEditorHandle } from "../../../../../shared/ui/form/input/NotionEditor";
import Button from "../../../../../shared/ui/button/Button";
import { Session } from "../../../domain/Session";
import { SessionTemplate, TemplateField, TemplateSelector } from "../../../../session_templates";
import { useSessionPreferences } from "../../../../center";
import Label from "../../../../../shared/ui/form/Label";
import DOMPurify from "dompurify";
import { buildTemplateHTML, parseTemplateHTML } from "../../utils/templateDocumentUtils";
import { notesToHtml } from "../../utils/notesUtils";
import { useTherapist } from "../../../../../context/useTherapist";
import { useNotesDictation } from "../../../application/useNotesDictation";
import DictationPunctuationHint from "../../../../../shared/ui/common/DictationPunctuationHint";

interface EditSessionNotesModalProps {
    isOpen: boolean;
    onClose: () => void;
    session: Session;
    onSuccess: () => void;
    template?: SessionTemplate | null;
}

export default function EditSessionNotesModal({
    isOpen,
    onClose,
    session,
    onSuccess,
    template: initialTemplate
}: EditSessionNotesModalProps) {
    const { t } = useTranslation();
    const { therapyFieldsEnabled } = useSessionPreferences();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [notes, setNotes] = useState(session.notes || "");
    const [template, setTemplate] = useState<SessionTemplate | null>(initialTemplate || null);
    const [isChangingTemplate, setIsChangingTemplate] = useState(false);
    const { therapist } = useTherapist();

    const [documentHTML, setDocumentHTML] = useState<string>("");
    const [showReference, setShowReference] = useState(true);
    /** Frozen snapshot of the notes as they were when the modal opened — never mutated by editing */
    const [originalNotes, setOriginalNotes] = useState(session.notes || "");
    const [hasManuallyChangedTemplate, setHasManuallyChangedTemplate] = useState(false);

    const editorRef = useRef<NotionEditorHandle>(null);

    // What is being heard is shown provisionally and then *replaced* by the
    // settled phrase — writing both is what duplicated every spoken sentence.
    const showInterim = useCallback((text: string) => {
        editorRef.current?.setInterimText(text);
    }, []);
    const writeSettled = useCallback((text: string) => {
        editorRef.current?.commitInterim(text);
    }, []);
    const dictationLanguage = therapist?.language === "es" ? "es" : "en";
    const dictation = useNotesDictation({
        language: dictationLanguage,
        onInterim: showInterim,
        onFinal: writeSettled,
    });
    const stopDictation = dictation.stop;

    const toggleDictation = () => {
        if (!dictation.supported) {
            alert(t("session.dictation.notSupported", "Speech recognition is not supported in this browser."));
            return;
        }
        dictation.toggle();
    };

    useEffect(() => {
        if (isOpen) {
            const raw = session.notes || "";
            setNotes(raw);
            setOriginalNotes(raw);  // Freeze the reference snapshot

            // Only update template from prop if it's actually provided
            if (initialTemplate) {
                setTemplate(initialTemplate);
            }

            setIsChangingTemplate(false);
            setHasManuallyChangedTemplate(false);
            setShowReference(true);
        } else {
            // Closing the modal must close the microphone with it.
            stopDictation();
        }
    }, [isOpen, session.id, session.notes, initialTemplate, stopDictation]);

    // Auto-load template if session has template_id but prop is missing
    useEffect(() => {
        const loadTemplate = async () => {
            if (!isOpen || template) return;

            // Case 1: session has template_id — just fetch that template
            if (session?.template_id) {
                try {
                    setLoading(true);
                    const tpl = await getSessionTemplate(session.template_id);
                    setTemplate(tpl);
                } catch (err) {
                    console.error("Error auto-loading template in modal:", err);
                } finally {
                    setLoading(false);
                }
                return;
            }

            // Case 2: no template_id but notes look structured — try to detect from headers
            const raw = session.notes || "";
            const headerMatches = [...raw.matchAll(/\*\*(.+?)\*\*/g)].map(m => m[1].trim().toLowerCase());
            if (headerMatches.length === 0) return; // plain notes, no detection needed

            try {
                setLoading(true);
                const allTemplates = await listSessionTemplates();
                for (const tplMeta of allTemplates) {
                    if (!tplMeta.id) continue;
                    const tpl = await getSessionTemplate(tplMeta.id);
                    if (!tpl?.fields?.length) continue;
                    const fieldNames = tpl.fields.map((f: TemplateField) => f.name.trim().toLowerCase());
                    // If every field name matches a header in the notes, this is our template
                    const allMatch = fieldNames.every((name: string) => headerMatches.includes(name));
                    if (allMatch) {
                        setTemplate(tpl);
                        break;
                    }
                }
            } catch (err) {
                console.error("Error auto-detecting template from notes:", err);
            } finally {
                setLoading(false);
            }
        };
        loadTemplate();
    }, [isOpen, template, session?.template_id, session?.notes]);

    // Build unified HTML document when template initialises
    useEffect(() => {
        if (!isOpen || !template || !template.fields?.length) return;

        const fields = template.fields;
        const values: Record<string, string> = {};

        // 1. Prefer template_data from analysis
        if (session.analysis?.template_data?.length && !hasManuallyChangedTemplate) {
            fields.forEach((field: TemplateField) => {
                const data = session.analysis?.template_data?.find(d =>
                    d.template_field_id === field.id || d.template_field_id === field.name
                );
                if (data) values[field.id!] = data.value;
            });
        }

        // 2. Fallback: try to parse existing unified HTML or legacy markdown notes
        if (!Object.keys(values).length && originalNotes && !hasManuallyChangedTemplate) {
            const parsed = parseTemplateHTML(originalNotes, fields);
            const hasContent = Object.values(parsed).some(v => v && v !== "<p></p>");
            if (hasContent) {
                Object.assign(values, parsed);
            } else {
                // legacy **Field**: value format
                fields.forEach(field => {
                    const esc = field.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                    const re = new RegExp(`\\*\\*${esc}\\*\\*\\s*:?`, 'i');
                    const m = originalNotes.match(re);
                    if (m && m.index !== undefined) {
                        const start = m.index + m[0].length;
                        let end = originalNotes.length;
                        fields.forEach(nf => {
                            if (nf.name === field.name) return;
                            const ne = nf.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                            const nm = originalNotes.substring(start).match(new RegExp(`\\*\\*${ne}\\*\\*\\s*:?`, 'i'));
                            if (nm?.index !== undefined && start + nm.index < end) end = start + nm.index;
                        });
                        values[field.id!] = originalNotes.substring(start, end).trim();
                    } else {
                        values[field.id!] = "";
                    }
                });
            }
        }

        const newHtml = buildTemplateHTML(fields, values, t, therapist?.language);

        if (hasManuallyChangedTemplate) {
            setDocumentHTML(newHtml);
            setNotes(newHtml);
        }
    }, [template, isOpen, session.analysis?.template_data, originalNotes, t, therapist?.language, hasManuallyChangedTemplate]);

    const handleDocumentChange = useCallback((html: string) => {
        setDocumentHTML(html);
        setNotes(html); // notes = the full document HTML
    }, []);

    const handleTemplateSelect = async (templateId: string) => {
        try {
            setLoading(true);
            const tpl = await getSessionTemplate(templateId);
            setTemplate(tpl);
            setHasManuallyChangedTemplate(true);
            setIsChangingTemplate(false);
        } catch (err) {
            console.error("Error fetching template detail:", err);
            setError(t("templates.loadError", "Could not load template details"));
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        setLoading(true);
        setError(null);
        try {
            await sessionService.updateSession(session.id, {
                notes,
                template_id: template?.id || undefined
            });
            onSuccess();
            onClose();
        } catch (err: unknown) {
            console.error(err);
            setError(t("common.error") || "Error saving notes. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
            {/* Overlay */}
            <div
                className="absolute inset-0 bg-black/40 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Modal */}
            <div className={`relative w-full ${template && showReference ? 'max-w-7xl' : 'max-w-4xl'} rounded-2xl border border-gray-200 bg-white p-6 shadow-xl dark:border-gray-700 dark:bg-gray-900 max-h-[95vh] flex flex-col transition-all duration-300`}>
                {/* Close */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-content-tertiary hover:text-gray-600 dark:hover:text-gray-300 z-10"
                >
                    <X className="w-5 h-5" />
                </button>

                <h2 className="mb-6 text-xl font-semibold text-content-primary">
                    {t("session.editSessionDetails")}
                </h2>

                <div className="flex-1 overflow-y-auto">
                    <div className="flex flex-col xl:flex-row gap-6 xl:gap-8">
                        {/* Main Body */}
                        <div className="flex-1 space-y-6">
                            {/* Template Section */}
                            <div>
                                <Label className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-2 block">
                                    {t("templates.activeTemplate")}
                                </Label>

                                {!isChangingTemplate ? (
                                    <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-gray-700">
                                        {/* Current template badge */}
                                        <div className={`flex items-center gap-2 flex-1 ${!template ? 'text-content-tertiary italic' : ''}`}>
                                            {template ? (
                                                <>
                                                    <span className="w-2 h-2 rounded-full bg-brand-500 flex-shrink-0" />
                                                    <span className="text-sm font-semibold text-gray-800 dark:text-white">{template.name}</span>
                                                    {template.description && (
                                                        <span className="text-xs text-content-tertiary truncate hidden sm:inline">— {template.description}</span>
                                                    )}
                                                </>
                                            ) : (
                                                <span className="text-sm">{t("templates.noTemplate", "No template selected")}</span>
                                            )}
                                        </div>
                                        {/* Change link: only where the centre offers templates */}
                                        {therapyFieldsEnabled && (
                                        <button
                                            onClick={() => setIsChangingTemplate(true)}
                                            className="text-xs text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300 font-medium flex items-center gap-1 flex-shrink-0 transition-colors"
                                        >
                                            <Edit2 className="w-3 h-3" />
                                            {t("common.change")}
                                        </button>
                                        )}
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        <TemplateSelector
                                            value={template?.id || ""}
                                            onChange={handleTemplateSelect}
                                            currentTemplate={template}
                                        />
                                        <button
                                            onClick={() => setIsChangingTemplate(false)}
                                            className="text-xs text-content-tertiary hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                                        >
                                            {t("common.cancel")}
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Dictation controls */}
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={toggleDictation}
                                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg font-semibold text-xs uppercase tracking-wider transition-all
                                        ${dictation.isDictating
                                            ? "bg-purple-100 text-purple-700 ring-2 ring-purple-500/20"
                                            : "bg-white border border-gray-200 text-gray-600 hover:border-purple-200 hover:text-purple-600"}`}
                                >
                                    {dictation.isDictating && (
                                        <motion.span
                                            animate={{ scale: [1, 1.2, 1], opacity: [0.5, 1, 0.5] }}
                                            transition={{ repeat: Infinity, duration: 1.5 }}
                                            className="w-2 h-2 rounded-full bg-purple-600"
                                        />
                                    )}
                                    {dictation.isDictating ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                                    {dictation.isDictating ? t("session.dictation.stop") : t("session.dictation.start")}
                                </button>
                                <DictationPunctuationHint language={dictationLanguage} />
                            </div>

                            {template && (template.fields?.length ?? 0) > 0 ? (
                                // Unified document view
                                <div className="mt-6">
                                    <NotionEditor
                                        ref={editorRef}
                                        value={hasManuallyChangedTemplate ? documentHTML : notes}
                                        onChange={handleDocumentChange}
                                        placeholder={t("notionEditor.placeholder")}
                                        className="min-h-[360px]"
                                    />
                                </div>
                            ) : (
                                // Standard View
                                <div>
                                    <NotionEditor
                                        ref={editorRef}
                                        placeholder={t("session.notesPlaceholder")}
                                        value={notes}
                                        onChange={setNotes}
                                        hint={t("session.notesHint")}
                                    />
                                </div>
                            )}

                            {error && <p className="text-red-500 text-sm text-center">{error}</p>}
                        </div>

                        {/* Reference Panel */}
                        {template && showReference && (
                            <div className="w-full xl:w-96 border-t xl:border-t-0 xl:border-l border-gray-100 dark:border-gray-800 pt-6 xl:pt-0 xl:pl-8 animate-in slide-in-from-right-4 duration-300">
                                <div className="sticky top-0">
                                    <h3 className="text-sm font-bold text-content-primary uppercase tracking-wider mb-4 border-b dark:border-gray-800 pb-2 flex items-center gap-2">
                                        <Search className="w-4 h-4 text-content-tertiary" />
                                        {t("session.referenceTitle")}
                                    </h3>
                                    <div className="bg-gray-50 dark:bg-white/5 rounded-xl p-4 border border-gray-100 dark:border-gray-800 max-h-[40vh] xl:max-h-[70vh] overflow-y-auto notion-viewer">
                                        <div
                                            className="notion-viewer-content"
                                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(notesToHtml(originalNotes), { ADD_ATTR: ["data-field-id"] }) }}
                                        />
                                    </div>
                                    <p className="mt-4 text-xs text-content-tertiary italic leading-snug">
                                        {t("session.referenceHint", "Use this panel to copy-paste or verify content during template migration.")}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="mt-8 flex justify-end gap-3 pt-6 pb-10 sm:pb-0 border-t border-gray-100 dark:border-gray-800">
                    <Button variant="outline" onClick={onClose} disabled={loading}>
                        {t("common.cancel")}
                    </Button>
                    <Button onClick={handleSubmit} disabled={loading}>
                        {loading ? t("common.loading") : t("common.save")}
                    </Button>
                </div>
            </div>
        </div>
    );
}
